<script setup lang="ts">
const { user, isAdmin, hasOverwrittenUserRoles } = useUser()
const { logout } = useAuth()
</script>

<template>
  <KernelPopover
    v-if="user"
    align="block-end"
    class="user-popover"
  >
    <template #trigger="popoverAttrs">
      <button
        class="user-button"
        v-bind="popoverAttrs"
      >
        <span>{{ user.displayName }}</span>
        <KernelBadgeAnchor
          v-slot="badgeAttrs"
          align-block="start"
          :hidden="!hasOverwrittenUserRoles"
          color="signal-orange"
          size="nano"
          icon="key-vertical-rounded"
        >
          <KernelAvatar
            v-tooltip="hasOverwrittenUserRoles ? $t('accounts.overridenRoles') : ''"
            v-bind="badgeAttrs"
            :image-url="user?.photoURL"
            size="medium"
            :name="user.displayName"
          />
        </KernelBadgeAnchor>
      </button>
    </template>

    <KernelItem>
      <template #start>
        <KernelAvatar
          :image-url="user?.photoURL"
          size="large"
          :name="user.displayName"
        />
      </template>

      {{ user.displayName }}

      <AdminBadge
        v-if="isAdmin"
      />

      <template #subtitle>
        {{ user.email }} <br>
      </template>

      <template #end>
        <KernelActionButton
          v-if="isAdmin"
          v-dialog:override-roles
          v-tooltip="$t('accounts.overrideRoles')"
          icon="person-edit-rounded"
          type="tertiary"
        />
      </template>
    </KernelItem>

    <KernelButtonGroup>
      <KernelActionButton
        type="primary"
        icon="logout-rounded"
        autofocus
        @click="logout({ redirect: true })"
      >
        {{ $t('accounts.logout') }}
      </KernelActionButton>
    </KernelButtonGroup>
  </KernelPopover>
</template>

<style scoped>
hr {
  margin-block: 0.5rem;
}

ul {
  padding-inline-start: 1rem;
  list-style: disc;
  color: var(--kernel-shade-800);
  font: var(--kernel-font-body-2);
}

.user-button {
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font: var(--kernel-font-body-3);
  border-radius: var(--kernel-border-radius-rounded);
  transition: color 250ms ease,  font-weight 250ms ease;
  padding-inline-start: 1rem;

  &:hover,
  &:focus-visible {
    color: var(--kernel-primary);
    font-weight: 600;
  }

  &:focus-visible {
    outline: 1px solid var(--kernel-primary);
  }
}

:global(.user-popover) {
  --popover-offset: 0.5rem;
}
</style>
